// 📁 hooks/users/useUsers.js
import { useState, useEffect, useCallback, useMemo } from "react";
import apiClient from "utils/apiClient";

const useUsers = () => {
  const [state, setState] = useState({
    users: [],
    loading: false,
    error: null,
    pagination: {
      page: 0,
      size: 10,
      totalElements: 0,
      totalPages: 0,
      first: true,
      last: false,
    },
  });

  const authToken = sessionStorage.getItem("authToken");

  const headers = useMemo(
    () => ({
      Authorization: `Bearer ${authToken}`,
      "Content-Type": "application/json",
    }),
    [authToken]
  );

  /** -------------------- Generic Helpers -------------------- **/

  const getRequest = useCallback(
    async (url) => {
      setState((prev) => ({ ...prev, loading: true, error: null }));
      try {
        const response = await apiClient.get(url, { headers });
        return response.data;
      } catch (err) {
        const errorMessage = err.response?.data?.message || err.message;
        console.error("[useUsers:getRequest]", errorMessage);
        setState((prev) => ({ ...prev, error: errorMessage }));
        throw new Error(errorMessage);
      } finally {
        setState((prev) => ({ ...prev, loading: false }));
      }
    },
    [headers]
  );

  const postRequest = useCallback(
    async (url, payload) => {
      setState((prev) => ({ ...prev, loading: true, error: null }));
      try {
        const response = await apiClient.post(url, payload, { headers });
        return response.data;
      } catch (err) {
        const errorMessage = err.response?.data?.message || err.message;
        setState((prev) => ({ ...prev, error: errorMessage }));
        throw new Error(errorMessage);
      } finally {
        setState((prev) => ({ ...prev, loading: false }));
      }
    },
    [headers]
  );

  const putRequest = useCallback(
    async (url, payload) => {
      setState((prev) => ({ ...prev, loading: true, error: null }));
      try {
        const response = await apiClient.put(url, payload, { headers });
        return response.data;
      } catch (err) {
        const errorMessage = err.response?.data?.message || err.message;
        setState((prev) => ({ ...prev, error: errorMessage }));
        throw new Error(errorMessage);
      } finally {
        setState((prev) => ({ ...prev, loading: false }));
      }
    },
    [headers]
  );

  const patchRequest = useCallback(
    async (url, payload) => {
      setState((prev) => ({ ...prev, loading: true, error: null }));
      try {
        console.log("[useUsers] PATCH", url, payload);
        const response = await apiClient.patch(url, payload, { headers });
        console.log("[useUsers] PATCH response", url, response?.data);
        return response.data;
      } catch (err) {
        const errorMessage = err.response?.data?.message || err.message;
        setState((prev) => ({ ...prev, error: errorMessage }));
        throw new Error(errorMessage);
      } finally {
        setState((prev) => ({ ...prev, loading: false }));
      }
    },
    [headers]
  );

  const deleteRequest = useCallback(
    async (url) => {
      // no data param
      setState((prev) => ({ ...prev, loading: true, error: null }));
      try {
        const response = await apiClient.delete(url, { headers }); // no data field
        return response.data;
      } catch (err) {
        const errorMessage = err.response?.data?.message || err.message;
        setState((prev) => ({ ...prev, error: errorMessage }));
        throw new Error(errorMessage);
      } finally {
        setState((prev) => ({ ...prev, loading: false }));
      }
    },
    [headers]
  );

  /** -------------------- Fetch All Users -------------------- **/

  const fetchUsers = useCallback(async () => {
    try {
      setState((prev) => ({ ...prev, loading: true, error: null }));

      const pageSize = 50;
      const baseUrl = "/profile-service/api/v1/user-profiles";

      // Fetch first page
      const firstResponse = await apiClient.get(
        `${baseUrl}?page=0&size=${pageSize}&refresh=true`
      );
      const { content: firstContent = [], totalPages = 1 } = firstResponse.data;

      let allUsers = [...firstContent];

      // Fetch remaining pages if needed
      if (totalPages > 1) {
        const promises = [];
        for (let p = 1; p < totalPages; p++) {
          promises.push(
            apiClient.get(
              `${baseUrl}?page=${p}&size=${pageSize}&refresh=true`,
              {
                headers,
              }
            )
          );
        }
        const responses = await Promise.all(promises);
        responses.forEach((res) => {
          allUsers = allUsers.concat(res.data.content || []);
        });
      }

      const organizedUsers = allUsers.map(formatUserData);

      setState({
        users: organizedUsers,
        loading: false,
        error: null,
        pagination: {
          page: 0,
          size: organizedUsers.length,
          totalElements: organizedUsers.length,
          totalPages,
          first: true,
          last: true,
        },
      });
    } catch (err) {
      console.error("[useUsers] Error fetching users:", err);
      setState((prev) => ({
        ...prev,
        loading: false,
        error: "Failed to load users. Please try again later.",
      }));
    }
  }, [headers]);

  /** -------------------- Fetch Single User -------------------- **/

  const getUserById = useCallback(
    async (userId) => {
      if (!userId) throw new Error("User ID is required");
      const data = await getRequest(
        `/profile-service/api/v1/user-profiles/${userId}?refresh=true`
      );
      return formatUserData(data);
    },
    [getRequest]
  );

  /** -------------------- CRUD & Verification -------------------- **/

  const createUser = async (userData, profileData) => {
    console.log("🚀 [createUser] Starting user creation process...");
    console.log(
      "📝 [createUser] Step 1: Creating identity record in identity-service"
    );
    console.log(
      "📦 [createUser] Identity payload:",
      JSON.stringify(userData, null, 2)
    );

    // Step 1: Create identity record first
    const user = await postRequest(
      "/identity-service/api/v1/auth/signup",
      userData
    );

    if (!user?.id) {
      console.error(
        "❌ [createUser] Identity creation failed: No user ID returned"
      );
      throw new Error("User ID not returned from signup");
    }

    console.log(
      "✅ [createUser] Step 1 Complete: Identity created successfully"
    );
    console.log("🆔 [createUser] Created user ID:", user.id);
    console.log(
      "📋 [createUser] Identity response:",
      JSON.stringify(user, null, 2)
    );

    // Step 2: Build profile payload with the user ID from identity service
    console.log(
      "📝 [createUser] Step 2: Building profile payload with user ID"
    );
    const profilePayload = buildProfilePayload(user, userData, profileData);
    console.log(
      "📦 [createUser] Profile payload:",
      JSON.stringify(profilePayload, null, 2)
    );

    // Step 3: Create profile record using the identity user ID
    console.log(
      "📝 [createUser] Step 3: Creating profile record in profile-service"
    );
    const profile = await postRequest(
      "/profile-service/api/v1/user-profiles",
      profilePayload
    );

    console.log(
      "✅ [createUser] Step 3 Complete: Profile created successfully"
    );
    console.log(
      "📋 [createUser] Profile response:",
      JSON.stringify(profile, null, 2)
    );
    console.log("🎉 [createUser] User creation completed successfully!");
    console.log("👤 [createUser] Final user ID:", user.id);

    return { success: true, user, profile };
  };

  const patchIdentityProperties = (userId, propertiesArray) =>
    patchRequest(
      `/identity-service/api/v1/users/props?uid=${userId}`,
      propertiesArray
    );

  const patchProfileProperties = (userId, propertiesArray) =>
    patchRequest(
      `/profile-service/api/v1/user-profiles/props?uid=${userId}`,
      propertiesArray
    );

  const verifyUserIdentity = (userId) =>
    postRequest(
      `/profile-service/api/v1/user-profiles/identity-verification/user/${userId}/verify`,
      {}
    );

  const verifyWorkAuthorization = (userId) =>
    postRequest(
      `/profile-service/api/v1/user-profiles/work-authorization/user/${userId}/verify`,
      {}
    );

  const deleteUserProfileProperty = (userId) =>
    deleteRequest(`/profile-service/api/v1/user-profiles?id=${userId}`);

  /** -------------------- Auto Load -------------------- **/

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  /** -------------------- Return Object -------------------- **/

  const { users, loading, error, pagination } = state;

  return {
    users,
    loading,
    error,
    pagination,
    refetch: fetchUsers,
    totalUsers: pagination.totalElements,
    activeUsers: users.filter((u) => u.status === "ACTIVE").length,
    verifiedUsers: users.filter((u) => u.verified).length,

    // Single user management
    getUserById,
    createUser,
    patchUserProperties: patchIdentityProperties,
    patchProfileProperties,
    verifyUserIdentity,
    verifyWorkAuthorization,
    deleteUserProfileProperty,
  };
};

/** -------------------- Helpers -------------------- **/

function formatUserData(user = {}) {
  return {
    id: user.id || "None",
    username: user.username || "None",
    email: user.email || "None",
    phone: user.phone || "None",
    firstName: user.firstName || "None",
    lastName: user.lastName || "None",
    gender: user.gender || null,
    fullName: `${user.firstName || ""} ${user.lastName || ""}`.trim() || "None",
    profilePhoto: user.profilePhoto || null,
    birthDate: user.birthDate || null,
    ssn: user.ssn || null,
    securityPhoto: user.securityPhoto || null,
    verified: Boolean(user.verified),
    status: user.status || "UNKNOWN",
    userIdentityVerification: user.userIdentityVerification || "pending",
    userWorkAuthorization: user.userWorkAuthorization || "None",
    completeProfile: computeProfileCompleteness(user),

    permanentAddress: user.permanentAddress || null,
    residentialAddress: user.residentialAddress || null,
    workAddress: user.workAddress || null,
    schoolAddress: user.schoolAddress || null,
    billingAddress: user.billingAddress || null,

    // Optionally preferred mailing address & contact method
    preferredMailingAddress: user.preferredMailingAddress || null,
    preferredContactMethod: user.preferredContactMethod || null,
  };
}

function computeProfileCompleteness(user) {
  const required = ["firstName", "lastName", "email", "phone"];
  const filled = required.filter((key) => user[key]);
  return Math.round((filled.length / required.length) * 100);
}

function buildProfilePayload(user, userData, profileData) {
  console.log("🔨 [buildProfilePayload] Building profile payload...");
  console.log(
    "📥 [buildProfilePayload] User (from identity):",
    JSON.stringify(user, null, 2)
  );
  console.log(
    "📥 [buildProfilePayload] UserData (identity fields):",
    JSON.stringify(userData, null, 2)
  );
  console.log(
    "📥 [buildProfilePayload] ProfileData (profile fields):",
    JSON.stringify(profileData, null, 2)
  );

  // Build nested contact details object
  const contactDetails = {
    mobilePhone:
      profileData.contactDetails?.mobilePhone || userData.phone || "",
    homePhone: profileData.contactDetails?.homePhone || "",
    workPhone: profileData.contactDetails?.workPhone || "",
    personalEmail:
      profileData.contactDetails?.personalEmail || userData.email || "",
    email:
      profileData.contactDetails?.email ||
      profileData.workEmail ||
      userData.email ||
      "",
    website: profileData.contactDetails?.website || "",
    twitter: profileData.contactDetails?.twitter || "",
    facebook: profileData.contactDetails?.facebook || "",
    linkedin: profileData.contactDetails?.linkedin || "",
  };

  // Build nested address objects (never null)
  const buildAddress = (prefix) => ({
    street: profileData[`${prefix}Address`]?.street || "",
    city: profileData[`${prefix}Address`]?.city || "",
    state: profileData[`${prefix}Address`]?.state || "",
    zipCode: profileData[`${prefix}Address`]?.zipCode || "",
    country: profileData[`${prefix}Address`]?.country || "",
  });

  const permanentAddress = buildAddress("permanent");
  const residentialAddress = buildAddress("residential");
  const workAddress = buildAddress("work");
  const schoolAddress = buildAddress("school");
  const billingAddress = buildAddress("billing");

  const payload = {
    id: user.id, // Use the ID from identity service
    firstName: userData.firstName,
    middleName: userData.middleName || "",
    lastName: userData.lastName,
    username: userData.username,
    // include profilePhoto when provided by the client
    profilePhoto: profileData.profilePhoto || null,
    birthDate: profileData.birthDate || "",
    gender: profileData.gender ? profileData.gender.toUpperCase() : null,
    ssn: profileData.ssn || null,
    status: profileData.status || userData.status || "ACTIVE",
    nextOfKin: profileData.nextOfKin || null,
    identificationDocumentType: profileData.identificationDocumentType || null,
    userWorkAuthorization: profileData.userWorkAuthorization || null,
    contactDetails,

    // Always include addresses even if empty
    permanentAddress,
    residentialAddress,
    workAddress,
    schoolAddress,
    billingAddress,

    preferredMailingAddress: profileData.preferredMailingAddress || null,
    preferredContactMethod: profileData.preferredContactMethod || null,
  };

  // Remove null/empty values for cleaner payload, but keep address objects always
  const cleanPayload = Object.fromEntries(
    Object.entries(payload).filter(([key, value]) => {
      if (value === null || value === "") return false;
      if (typeof value === "object" && !Array.isArray(value)) {
        if (
          [
            "permanentAddress",
            "residentialAddress",
            "workAddress",
            "schoolAddress",
            "billingAddress",
          ].includes(key)
        ) {
          return true; // always keep address objects even if empty
        }
        return Object.values(value).some((val) => val !== null && val !== "");
      }
      return true;
    })
  );

  console.log(
    "📤 [buildProfilePayload] Final payload:",
    JSON.stringify(cleanPayload, null, 2)
  );
  return cleanPayload;
}

export default useUsers;
